function keyFor(item) {
    return typeof item === "string" ? item : item?.key;
}

function keysFor(items = []) {
    return items.map(keyFor).filter(Boolean);
}

function keyObject(items = []) {
    return Object.fromEntries(keysFor(items).map((key) => [key, true]));
}

function sameKeys(left = [], right = []) {
    const leftKeys = [...new Set(keysFor(left))].sort();
    const rightKeys = [...new Set(keysFor(right))].sort();
    return JSON.stringify(leftKeys) === JSON.stringify(rightKeys);
}

export function cloneHubGroup(group) {
    return {
        ...group,
        users: (group.users || []).map((user) =>
            typeof user === "string" ? user : { ...user },
        ),
    };
}

export function cloneHubCalendar(calendar) {
    return {
        ...calendar,
        events: (calendar.events || []).map((event) =>
            typeof event === "string" ? event : { ...event },
        ),
    };
}

export function buildHubDraftUpdates({
    hub,
    name,
    imageURL,
    selectedUsers,
    selectedManagers,
    hubGroups,
    hubCalendars,
    folders,
    files,
    groups,
    calendars,
    events,
    users,
}) {
    const updates = {};
    const hubKey = hub.key;
    const managerSelectionChanged = !sameKeys(
        hub.managers,
        selectedManagers,
    );
    const userSelectionChanged = !sameKeys(hub.users, selectedUsers);
    const selectedManagerKeys = keysFor(selectedManagers);
    const selectedUserKeys = keysFor(selectedUsers);
    const removedManagerKeys = keysFor(hub.managers).filter(
        (managerKey) => !selectedManagerKeys.includes(managerKey),
    );
    const removedUserKeys = keysFor(hub.users).filter(
        (userKey) => !selectedUserKeys.includes(userKey),
    );

    if (hub.name !== name) {
        updates[`folders/${hubKey}/name`] = name;
    }
    if (hub.imageURL !== imageURL) {
        updates[`folders/${hubKey}/imageURL`] = imageURL;
    }
    if (managerSelectionChanged) {
        updates[`folders/${hubKey}/managers`] = keyObject(selectedManagers);
    }
    if (userSelectionChanged) {
        updates[`folders/${hubKey}/users`] = keyObject(selectedUsers);
    }

    if (managerSelectionChanged) {
        (folders || [])
            .filter((folder) =>
                (folder.ancestors || []).includes(hubKey),
            )
            .forEach((folder) => {
                const managers = [
                    ...selectedManagerKeys,
                    ...keysFor(folder.managers).filter(
                        (managerKey) =>
                            !removedManagerKeys.includes(managerKey),
                    ),
                ];
                updates[`folders/${folder.key}/managers`] = keyObject(managers);
            });

        (files || [])
            .filter((file) => (file.ancestors || []).includes(hubKey))
            .forEach((file) => {
                const managers = [
                    ...selectedManagerKeys,
                    ...keysFor(file.managers).filter(
                        (managerKey) =>
                            !removedManagerKeys.includes(managerKey),
                    ),
                ];
                updates[`files/${file.key}/managers`] = keyObject(managers);
            });
    }

    if (userSelectionChanged) {
        (folders || [])
            .filter(
                (folder) =>
                    folder.hub &&
                    (folder.ancestors || []).includes(hubKey),
            )
            .forEach((folder) => {
                const folderUsers = [
                    ...selectedUserKeys,
                    ...keysFor(folder.users).filter(
                        (userKey) => !removedUserKeys.includes(userKey),
                    ),
                ];
                updates[`folders/${folder.key}/users`] =
                    keyObject(folderUsers);

                (groups || [])
                    .filter((group) => group.hub === folder.key)
                    .forEach((group) => {
                        const groupUsers =
                            group.name === "All Users"
                                ? folderUsers
                                : keysFor(group.users).filter(
                                      (userKey) =>
                                          !removedUserKeys.includes(userKey),
                                  );
                        updates[`groups/${group.key}/users`] =
                            keyObject(groupUsers);
                    });
            });
    }

    if (managerSelectionChanged || userSelectionChanged) {
        // Enforce the invariant against the post-save state of the whole hub
        // subtree. Parent access changes are inherited by descendants, while
        // descendants can still have users and managers of their own.
        const affectedHubs = [
            {
                managers: selectedManagerKeys,
                users: selectedUserKeys,
            },
            ...(folders || [])
                .filter(
                    (folder) =>
                        folder.hub &&
                        (folder.ancestors || []).includes(hubKey),
                )
                .map((folder) => ({
                    managers: managerSelectionChanged
                        ? [
                              ...selectedManagerKeys,
                              ...keysFor(folder.managers).filter(
                                  (managerKey) =>
                                      !removedManagerKeys.includes(managerKey),
                              ),
                          ]
                        : keysFor(folder.managers),
                    users: userSelectionChanged
                        ? [
                              ...selectedUserKeys,
                              ...keysFor(folder.users).filter(
                                  (userKey) =>
                                      !removedUserKeys.includes(userKey),
                              ),
                          ]
                        : keysFor(folder.users),
                })),
        ];
        const savedAdminsByUser = new Map(
            (users || []).map((user) => [
                user.key,
                new Set(keysFor(user.admins)),
            ]),
        );

        affectedHubs.forEach((affectedHub) => {
            affectedHub.users.forEach((userKey) => {
                const savedAdminKeys =
                    savedAdminsByUser.get(userKey) || new Set();

                affectedHub.managers.forEach((managerKey) => {
                    if (!savedAdminKeys.has(managerKey)) {
                        updates[`users/${userKey}/admins/${managerKey}`] = true;
                    }
                });
            });
        });
    }

    (hubGroups || []).forEach((draftGroup) => {
        const savedGroup = (groups || []).find(
            (group) => group.key === draftGroup.key,
        );

        if (draftGroup.toDelete) {
            if (savedGroup) {
                updates[`groups/${draftGroup.key}`] = null;
            }
            return;
        }

        const groupUsers =
            draftGroup.name === "All Users"
                ? selectedUsers
                : draftGroup.users;

        if (!savedGroup) {
            updates[`groups/${draftGroup.key}`] = {
                name: draftGroup.name,
                hub: hubKey,
                users: keyObject(groupUsers),
            };
            return;
        }

        if (savedGroup.name !== draftGroup.name) {
            updates[`groups/${draftGroup.key}/name`] = draftGroup.name;
        }
        if (!sameKeys(savedGroup.users, groupUsers)) {
            updates[`groups/${draftGroup.key}/users`] =
                keyObject(groupUsers);
        }
    });

    (hubCalendars || []).forEach((draftCalendar) => {
        const savedCalendar = (calendars || []).find(
            (calendar) => calendar.key === draftCalendar.key,
        );

        if (draftCalendar.toDelete) {
            if (!savedCalendar) {
                return;
            }

            updates[`calendars/${draftCalendar.key}`] = null;
            (events || [])
                .filter((event) => event.calendar === draftCalendar.key)
                .forEach((event) => {
                    updates[`events/${event.key}`] = null;
                    (files || []).forEach((file) => {
                        if (
                            (file.events || []).some(
                                (fileEvent) => keyFor(fileEvent) === event.key,
                            )
                        ) {
                            updates[`files/${file.key}/events/${event.key}`] =
                                null;
                        }
                    });
                });
            return;
        }

        if (!savedCalendar) {
            updates[`calendars/${draftCalendar.key}`] = {
                name: draftCalendar.name,
                hub: hubKey,
            };
            return;
        }

        if (savedCalendar.name !== draftCalendar.name) {
            updates[`calendars/${draftCalendar.key}/name`] =
                draftCalendar.name;
        }
    });

    return updates;
}
